// src/hooks/useResultData.ts
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckerResult } from "../types";
import { API_URL } from "../config";

type Args = {
  /** 後端建立工作回傳的 content_checker_id */
  id?: number | null;
  /** 只顯示圖片兜底時可傳（純 base64，不含 data uri 前綴） */
  imgBase64?: string | null;
};

export function useResultData(args: Args = {}) {
  const { id: idRaw, imgBase64 } = args;

  // 1) 正規化 id / image 狀態
  const hasValidId = useMemo(
    () =>
      typeof idRaw === "number" &&
      Number.isFinite(idRaw!) &&
      (idRaw as number) > 0,
    [idRaw]
  );

  const normalizedImgBase64 = useMemo(() => {
    if (!imgBase64) return "";
    // 移除可能多餘的逗號
    return imgBase64.startsWith(",") ? imgBase64.slice(1) : imgBase64;
  }, [imgBase64]);

  const dataUrlFromImg = useMemo(
    () =>
      normalizedImgBase64
        ? `data:image/jpeg;base64,${normalizedImgBase64}`
        : "",
    [normalizedImgBase64]
  );

  // 沒 id 但有 img => image-only（不打 API）
  const isImageOnly = useMemo(
    () => !hasValidId && !!normalizedImgBase64,
    [hasValidId, normalizedImgBase64]
  );

  // 2) 狀態
  const [data, setData] = useState<CheckerResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(
    () => !!hasValidId && !isImageOnly
  );
  const [refreshing, setRefreshing] = useState<boolean>(false);

  // 3) 防重入/卸載
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // 4) 取一次資料
  const fetchOnce = useCallback(
    async (silent = false) => {
      // 僅圖片兜底：不打 API
      if (isImageOnly) {
        if (mountedRef.current) {
          setLoading(false);
          setRefreshing(false);
          setError(null);
          setData(null);
        }
        return;
      }

      // 沒有效 id：回報錯誤，但不持續 loading
      if (!hasValidId) {
        if (mountedRef.current) {
          setError("缺少有效的檢查結果 ID。");
          setLoading(false);
          setRefreshing(false);
        }
        return;
      }

      if (inFlightRef.current) return; // 還在飛，略過
      inFlightRef.current = true;

      try {
        if (!silent && !data) setLoading(true);
        if (silent) setRefreshing(true);
        setError(null);

        const resp = await fetch(`${API_URL}/content-checker/${idRaw}`);
        if (!resp.ok) {
          const t = await resp.text().catch(() => "");
          throw new Error(`HTTP ${resp.status} ${resp.statusText}\n${t}`);
        }
        const json = (await resp.json()) as CheckerResult;
        if (mountedRef.current) setData(json);
      } catch (e) {
        if (mountedRef.current) {
          setError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
        inFlightRef.current = false;
      }
    },
    // 依賴 id / isImageOnly 變動時重建
    [idRaw, hasValidId, isImageOnly]
  );

  // 5) 初次抓（或 id / isImageOnly 改變）
  useEffect(() => {
    fetchOnce(false);
  }, [fetchOnce]);

  const refetch = useCallback(
    (silent = false) => fetchOnce(silent),
    [fetchOnce]
  );

  return {
    data,
    loading,
    refreshing,
    error,
    isImageOnly,
    dataUrlFromImg,
    refetch,
  };
}
